// Workspace-load reconcile of composer drafts (#610). Per scope the newest write wins, and a clear
// is a write too (a tombstone), so a stale copy is dropped here instead of pushed back to the server.

import { ERASED_PEER } from "./cache/scope.js";

export interface ServerDraft {
  scope: string;
  text: string;
  updatedAt: number;
}
export interface ServerDraftTombstone {
  scope: string;
  updatedAt: number;
}
// This device's copy of a scope; `text: ""` means it cleared the scope at `updatedAt`.
export interface ReconcileLocalEntry {
  scope: string;
  text: string;
  updatedAt: number;
}
export interface ReconcileResult {
  apply: Map<string, string>;
  pushBack: ReconcileLocalEntry[];
  // Local drafts an equal-or-newer server clear removed: scope → that clear's time. Never pushed.
  drop: Map<string, number>;
  clearBack: ReconcileLocalEntry[];
}

// The erased-peer mask is no conversation: nothing is ever keyed under it (CYBO-1880).
export const isErasedScope = (scope: string): boolean => scope === `dm:${ERASED_PEER}`;

export function reconcileDrafts(
  serverAll: ServerDraft[],
  localAll: ReconcileLocalEntry[],
  tombstonesAll: ServerDraftTombstone[] = [],
): ReconcileResult {
  const server = serverAll.filter((s) => !isErasedScope(s.scope));
  const local = localAll.filter((l) => !isErasedScope(l.scope));
  const tombstones = tombstonesAll.filter((t) => !isErasedScope(t.scope));
  const remote = new Map<string, ServerDraft>();
  for (const t of tombstones) remote.set(t.scope, { scope: t.scope, text: "", updatedAt: t.updatedAt }); // prettier-ignore
  for (const s of server) {
    const prev = remote.get(s.scope);
    if (!prev || s.updatedAt >= prev.updatedAt) remote.set(s.scope, s);
  }
  const localByScope = new Map(local.map((l) => [l.scope, l]));
  const out: ReconcileResult = { apply: new Map(), pushBack: [], drop: new Map(), clearBack: [] };

  for (const [scope, s] of remote) {
    const l = localByScope.get(scope);
    if (l && l.updatedAt > s.updatedAt) {
      if (l.text !== "") {
        out.apply.set(scope, l.text);
        out.pushBack.push(l);
      } else if (s.text !== "") out.clearBack.push(l);
    } else if (s.text !== "") out.apply.set(scope, s.text);
    else if (l && l.text !== "") out.drop.set(scope, s.updatedAt);
  }
  for (const l of local) {
    if (remote.has(l.scope) || l.text === "") continue;
    out.apply.set(l.scope, l.text);
    out.pushBack.push(l);
  }
  return out;
}
